import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import "./RestManStudioCollection.css";

const localEnvironmentListUrl = "http://localhost:3001/restman-studio/environments";
const localEnvironmentUrl = "http://localhost:3001/restman-studio/environment";
const environmentNames = [
  "irealnd_dev",
  "ireland_test",
  "ireland_prod",
  "scania_china_dev",
  "scania_china_test",
];

const createEnvironmentRow = (overrides = {}) => ({
  id: `${Date.now()}-${Math.random()}`,
  enabled: true,
  key: "",
  value: "",
  secret: false,
  ...overrides,
});

const createEnvironmentRows = (values = []) => {
  const rows = values.map((value) =>
    createEnvironmentRow({
      key: value.key || "",
      value: value.value || "",
      enabled: value.enabled !== false,
      secret: Boolean(value.secret),
    })
  );

  return rows.length ? rows : [createEnvironmentRow()];
};

function RestManStudioEnvironments() {
  const [environmentOptions, setEnvironmentOptions] = useState(environmentNames);
  const [selectedEnvironment, setSelectedEnvironment] = useState(
    environmentNames[0]
  );
  const [environmentData, setEnvironmentData] = useState(null);
  const [environmentValues, setEnvironmentValues] = useState([
    createEnvironmentRow(),
  ]);
  const [environmentStatus, setEnvironmentStatus] = useState(null);
  const [environmentError, setEnvironmentError] = useState("");
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const [isLoadingEnvironment, setIsLoadingEnvironment] = useState(false);
  const [isSavingEnvironment, setIsSavingEnvironment] = useState(false);

  const loadEnvironment = useCallback(async (environmentName) => {
    if (!environmentName) {
      return;
    }

    try {
      setIsLoadingEnvironment(true);
      setEnvironmentError("");
      const { data } = await axios.get(
        `${localEnvironmentUrl}?name=${encodeURIComponent(environmentName)}`
      );

      setEnvironmentData(data.environment);
      setEnvironmentValues(createEnvironmentRows(data.environment?.values || []));
      setRevealedSecrets({});
      setEnvironmentStatus({
        action: "loaded",
        environmentName: data.environment?.name || environmentName,
        relativePath: data.relativePath,
      });
    } catch (err) {
      setEnvironmentError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load environment file."
      );
    } finally {
      setIsLoadingEnvironment(false);
    }
  }, []);

  useEffect(() => {
    const loadEnvironmentOptions = async () => {
      let names = environmentNames;

      try {
        const { data } = await axios.get(localEnvironmentListUrl);
        const apiNames = (data.environments || [])
          .map((environment) =>
            typeof environment === "string" ? environment : environment.name
          )
          .filter(Boolean);

        names = apiNames.length ? apiNames : environmentNames;
        setEnvironmentOptions(names);
      } catch (err) {
        setEnvironmentOptions(environmentNames);
        setEnvironmentError(
          err.response?.data?.message ||
            err.message ||
            "Unable to load environment list."
        );
      }

      const defaultEnvironment = names[0] || environmentNames[0];
      setSelectedEnvironment(defaultEnvironment);
      loadEnvironment(defaultEnvironment);
    };

    loadEnvironmentOptions();
  }, [loadEnvironment]);

  const handleEnvironmentChange = async (event) => {
    const environmentName = event.target.value;

    setSelectedEnvironment(environmentName);
    await loadEnvironment(environmentName);
  };

  const updateEnvironmentRow = (id, field, value) => {
    setEnvironmentValues(
      environmentValues.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const removeEnvironmentRow = (id) => {
    if (environmentValues.length === 1) {
      setEnvironmentValues([createEnvironmentRow()]);
      return;
    }

    setEnvironmentValues(environmentValues.filter((row) => row.id !== id));
  };

  const toggleSecretReveal = (id) => {
    setRevealedSecrets((current) => ({
      ...current,
      [id]: !current[id],
    }));
  };

  const createEnvironmentPayloadValues = () =>
    environmentValues
      .filter((row) => row.key.trim())
      .map((row) => ({
        key: row.key.trim(),
        value: row.value,
        enabled: row.enabled,
        secret: Boolean(row.secret),
      }));

  const handleSaveEnvironment = async () => {
    setEnvironmentError("");
    setEnvironmentStatus(null);

    try {
      setIsSavingEnvironment(true);
      const { data } = await axios.post(localEnvironmentUrl, {
        name: selectedEnvironment,
        values: createEnvironmentPayloadValues(),
      });

      setEnvironmentData(data.environment);
      setEnvironmentValues(createEnvironmentRows(data.environment?.values || []));
      setRevealedSecrets({});
      setEnvironmentStatus({
        action: data.action,
        environmentName: data.environment?.name || selectedEnvironment,
        relativePath: data.relativePath,
      });
    } catch (err) {
      setEnvironmentError(
        err.response?.data?.message ||
          err.message ||
          "Unable to save environment file."
      );
    } finally {
      setIsSavingEnvironment(false);
    }
  };

  return (
    <main className="local-pulse-page">
      <section className="lpt-workspace lpt-environment-page">
        <header className="lpt-title-bar">
          <h1>RestMan Studio Environments</h1>
          <div className="lpt-title-actions">
            <label className="lpt-environment-select">
              Environment
              <select
                value={selectedEnvironment}
                disabled={isLoadingEnvironment}
                onChange={handleEnvironmentChange}
              >
                {environmentOptions.map((environmentName) => (
                  <option key={environmentName} value={environmentName}>
                    {environmentName}
                  </option>
                ))}
              </select>
            </label>
            <Link className="lpt-title-link" to="/RestManStudio">
              Request Builder
            </Link>
          </div>
        </header>

        <section className="lpt-panel">
          <div className="lpt-environment-toolbar">
            <button
              type="button"
              className="lpt-secondary-button"
              disabled={isLoadingEnvironment}
              onClick={() => loadEnvironment(selectedEnvironment)}
            >
              {isLoadingEnvironment ? "Loading" : "Reload"}
            </button>
            <button
              type="button"
              className="lpt-save-button"
              disabled={isSavingEnvironment}
              onClick={handleSaveEnvironment}
            >
              {isSavingEnvironment ? "Saving" : "Save Environment"}
            </button>
            <button
              type="button"
              className="lpt-secondary-button"
              onClick={() =>
                setEnvironmentValues([
                  ...environmentValues,
                  createEnvironmentRow(),
                ])
              }
            >
              Add Variable
            </button>
          </div>

          {environmentError && <p className="lpt-save-error">{environmentError}</p>}

          {environmentStatus && (
            <table className="lpt-preview-table lpt-environment-meta">
              <tbody>
                <tr>
                  <th>Environment</th>
                  <td>{environmentStatus.environmentName}</td>
                </tr>
                <tr>
                  <th>File</th>
                  <td>{environmentStatus.relativePath}</td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>{environmentStatus.action}</td>
                </tr>
                {environmentData?.date_created && (
                  <tr>
                    <th>Created</th>
                    <td>{environmentData.date_created}</td>
                  </tr>
                )}
                {environmentData?.date_modified && (
                  <tr>
                    <th>Modified</th>
                    <td>{environmentData.date_modified}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          <div className="lpt-table-scroll">
            <table className="lpt-edit-table lpt-environment-table">
              <thead>
                <tr>
                  <th className="lpt-check-column">Use</th>
                  <th>Variable</th>
                  <th>Value</th>
                  <th className="lpt-secret-column">Secret</th>
                  <th className="lpt-action-column">Action</th>
                </tr>
              </thead>
              <tbody>
                {environmentValues.map((row) => {
                  const isRevealed = Boolean(revealedSecrets[row.id]);
                  const isSecretMasked = row.secret && !isRevealed;

                  return (
                    <tr key={row.id}>
                      <td className="lpt-check-column">
                        <input
                          type="checkbox"
                          checked={row.enabled}
                          onChange={(event) =>
                            updateEnvironmentRow(
                              row.id,
                              "enabled",
                              event.target.checked
                            )
                          }
                          aria-label="Enable environment variable"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.key}
                          placeholder="Variable name"
                          onChange={(event) =>
                            updateEnvironmentRow(row.id, "key", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type={isSecretMasked ? "password" : "text"}
                          value={row.value}
                          placeholder="Variable value"
                          onChange={(event) =>
                            updateEnvironmentRow(
                              row.id,
                              "value",
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td className="lpt-secret-column">
                        <label className="lpt-inline-check">
                          <input
                            type="checkbox"
                            checked={row.secret}
                            onChange={(event) =>
                              updateEnvironmentRow(
                                row.id,
                                "secret",
                                event.target.checked
                              )
                            }
                          />
                          Secret
                        </label>
                      </td>
                      <td className="lpt-action-column lpt-environment-actions">
                        {row.secret && (
                          <button
                            type="button"
                            className="lpt-secondary-button"
                            onClick={() => toggleSecretReveal(row.id)}
                          >
                            {isRevealed ? "Hide" : "Reveal"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="lpt-secondary-button"
                          onClick={() => removeEnvironmentRow(row.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </section>
    </main>
  );
}

export default RestManStudioEnvironments;
